// mds-dojo — dijit/ColorPalette — FAITHFUL port of references/dijit/ColorPalette.js.
// (OVERWRITES the earlier freestyle-lite stub.) A keyboard-accessible color-picking
// grid (7x10 or 3x4) usable standalone or as a popup. Built on _Widget +
// _TemplatedMixin + _PaletteMixin; each cell is a dijit._Color dye (a dojo Color
// subclass) whose getValue() returns a hex string.
//
// ADAPTATIONS for this package:
//  - `dojo/text!./templates/ColorPalette.html` → `import template from './templates/ColorPalette.html?raw'`
//    (+ template copied into dijit/templates/).
//  - `dojo/i18n!dojo/nls/colors` translations → inlined `colorNames` map (the dojo
//    English nls/colors root — descriptive tooltip per color). `dojo/i18n` +
//    `i18n.getLocalization(...)` → that map directly.
//  - `require`/`require.toUrl(...)` dropped → literal image paths (only referenced in
//    high-contrast mode, which is inert here — see ./hccss).
//  - `dojo/_base/declare` → `../dojo/_base/declare-runtime`; other dojo ids → relative.
//  - `dojo/colors` imported for its side effect (extends Color.named with the CSS3
//    named colors the 7x10 grid references).
import template from './templates/ColorPalette.html?raw';
import _Widget from './_Widget'; // used also to load dijit/hccss for setting has("highcontrast")
import _TemplatedMixin from './_TemplatedMixin';
import _PaletteMixin from './_PaletteMixin';
import has from './hccss'; // has("highcontrast")
import Color from '../dojo/_base/Color'; // dojo.Color dojo.Color.named
import declare from '../dojo/_base/declare-runtime'; // declare
import domConstruct from '../dojo/dom-construct'; // domConstruct.place
import string from '../dojo/string'; // string.substitute
import '../dojo/colors'; // extend dojo.Color w/names of other colors

// module:
//		dijit/ColorPalette

// Inlined dojo/nls/colors (root/English): localized descriptive tooltip per color name.
var colorNames: any = {
	aliceblue: "alice blue", antiquewhite: "antique white", aqua: "aqua", aquamarine: "aquamarine",
	azure: "azure", beige: "beige", bisque: "bisque", black: "black", blanchedalmond: "blanched almond",
	blue: "blue", blueviolet: "blue-violet", brown: "brown", burlywood: "burlywood", cadetblue: "cadet blue",
	chartreuse: "chartreuse", chocolate: "chocolate", coral: "coral", cornflowerblue: "cornflower blue",
	cornsilk: "cornsilk", crimson: "crimson", cyan: "cyan", darkblue: "dark blue", darkcyan: "dark cyan",
	darkgoldenrod: "dark goldenrod", darkgray: "dark gray", darkgreen: "dark green", darkgrey: "dark gray",
	darkkhaki: "dark khaki", darkmagenta: "dark magenta", darkolivegreen: "dark olive green",
	darkorange: "dark orange", darkorchid: "dark orchid", darkred: "dark red", darksalmon: "dark salmon",
	darkseagreen: "dark sea green", darkslateblue: "dark slate blue", darkslategray: "dark slate gray",
	darkslategrey: "dark slate gray", darkturquoise: "dark turquoise", darkviolet: "dark violet",
	deeppink: "deep pink", deepskyblue: "deep sky blue", dimgray: "dim gray", dimgrey: "dim gray",
	dodgerblue: "dodger blue", firebrick: "fire brick", floralwhite: "floral white", forestgreen: "forest green",
	fuchsia: "fuchsia", gainsboro: "gainsboro", ghostwhite: "ghost white", gold: "gold", goldenrod: "goldenrod",
	gray: "gray", green: "green", greenyellow: "green-yellow", grey: "gray", honeydew: "honeydew",
	hotpink: "hot pink", indianred: "indian red", indigo: "indigo", ivory: "ivory", khaki: "khaki",
	lavender: "lavender", lavenderblush: "lavender blush", lawngreen: "lawn green", lemonchiffon: "lemon chiffon",
	lightblue: "light blue", lightcoral: "light coral", lightcyan: "light cyan",
	lightgoldenrodyellow: "light goldenrod yellow", lightgray: "light gray", lightgreen: "light green",
	lightgrey: "light gray", lightpink: "light pink", lightsalmon: "light salmon", lightseagreen: "light sea green",
	lightskyblue: "light sky blue", lightslategray: "light slate gray", lightslategrey: "light slate gray",
	lightsteelblue: "light steel blue", lightyellow: "light yellow", lime: "lime", limegreen: "lime green",
	linen: "linen", magenta: "magenta", maroon: "maroon", mediumaquamarine: "medium aquamarine",
	mediumblue: "medium blue", mediumorchid: "medium orchid", mediumpurple: "medium purple",
	mediumseagreen: "medium sea green", mediumslateblue: "medium slate blue",
	mediumspringgreen: "medium spring green", mediumturquoise: "medium turquoise",
	mediumvioletred: "medium violet-red", midnightblue: "midnight blue", mintcream: "mint cream",
	mistyrose: "misty rose", moccasin: "moccasin", navajowhite: "navajo white", navy: "navy", oldlace: "old lace",
	olive: "olive", olivedrab: "olive drab", orange: "orange", orangered: "orange red", orchid: "orchid",
	palegoldenrod: "pale goldenrod", palegreen: "pale green", paleturquoise: "pale turquoise",
	palevioletred: "pale violet-red", papayawhip: "papaya whip", peachpuff: "peach puff", peru: "peru",
	pink: "pink", plum: "plum", powderblue: "powder blue", purple: "purple", red: "red", rosybrown: "rosy brown",
	royalblue: "royal blue", saddlebrown: "saddle brown", salmon: "salmon", sandybrown: "sandy brown",
	seagreen: "sea green", seashell: "seashell", sienna: "sienna", silver: "silver", skyblue: "sky blue",
	slateblue: "slate blue", slategray: "slate gray", slategrey: "slate gray", snow: "snow",
	springgreen: "spring green", steelblue: "steel blue", tan: "tan", teal: "teal", thistle: "thistle",
	tomato: "tomato", transparent: "transparent", turquoise: "turquoise", violet: "violet", wheat: "wheat",
	white: "white", whitesmoke: "white smoke", yellow: "yellow", yellowgreen: "yellow green"
};

var ColorPalette: any = declare("dijit.ColorPalette", [_Widget, _TemplatedMixin, _PaletteMixin], {
	// summary:
	//		A keyboard accessible color-picking widget
	// description:
	//		Grid showing various colors, so the user can pick a certain color.
	//		Can be used standalone, or as a popup.

	// palette: [const] String
	//		Size of grid, either "7x10" or "3x4".
	palette: "7x10",

	// _palettes: [protected] Map
	//		This represents the value of the colors.
	//		The first level is a hashmap of the different palettes available.
	//		The next two dimensions represent the columns and rows of colors.
	_palettes: {
		"7x10": [
			["white", "seashell", "cornsilk", "lemonchiffon", "lightyellow", "palegreen", "paleturquoise", "lightcyan", "lavender", "plum"],
			["lightgray", "pink", "bisque", "moccasin", "khaki", "lightgreen", "lightseagreen", "lightskyblue", "cornflowerblue", "violet"],
			["silver", "lightcoral", "sandybrown", "orange", "palegoldenrod", "chartreuse", "mediumturquoise", "skyblue", "mediumslateblue", "orchid"],
			["gray", "red", "orangered", "darkorange", "yellow", "limegreen", "darkseagreen", "royalblue", "slateblue", "mediumorchid"],
			["dimgray", "crimson", "chocolate", "coral", "gold", "forestgreen", "seagreen", "blue", "blueviolet", "darkorchid"],
			["darkslategray", "firebrick", "saddlebrown", "sienna", "olive", "green", "darkcyan", "mediumblue", "darkslateblue", "darkmagenta"],
			["black", "darkred", "maroon", "brown", "darkolivegreen", "darkgreen", "midnightblue", "navy", "indigo", "purple"]
		],

		"3x4": [
			["white", "lime", "green", "blue"],
			["silver", "yellow", "fuchsia", "navy"],
			["gray", "red", "purple", "black"]
		]
	},

	// templateString: String
	//		The template of this widget.
	templateString: template,

	baseClass: "dijitColorPalette",

	_dyeFactory: function (value: any, row: any, col: any, title: any) {
		// Overrides _PaletteMixin._dyeFactory().
		return new this._dyeClass(value, row, col, title);
	},

	buildRendering: function () {
		// Instantiate the template, which makes a skeleton into which we'll insert a bunch of
		// <img> nodes
		this.inherited(arguments);

		//	Creates customized constructor for dye class (color of a single cell) for
		//	specified palette and high-contrast vs. normal mode.   Used in _getDye().
		this._dyeClass = declare(ColorPalette._Color, {
			palette: this.palette
		});

		// Creates <img> nodes in each cell of the template.
		this._preparePalette(
			this._palettes[this.palette],
			colorNames);
	}
});

ColorPalette._Color = declare("dijit._Color", Color, {
	// summary:
	//		Object associated with each cell in a ColorPalette palette.
	//		Implements dijit/Dye.

	// Template for each cell in normal (non-high-contrast mode).  Each cell contains a wrapper
	// node for showing the border (called dijitPaletteImg for back-compat), and dijitColorPaletteSwatch
	// for showing the color.
	template: "<span class='dijitInline dijitPaletteImg'>" +
		"<img src='${blankGif}' alt='${alt}' title='${title}' class='dijitColorPaletteSwatch' style='background-color: ${color}'/>" +
		"</span>",

	// Template for each cell in high contrast mode.  Each cell contains an image with the whole palette,
	// but scrolled and clipped to show the correct color only
	hcTemplate: "<span class='dijitInline dijitPaletteImg' style='position: relative; overflow: hidden; height: 12px; width: 14px;'>" +
		"<img src='${image}' alt='${alt}' title='${title}' style='position: absolute; left: ${left}px; top: ${top}px; ${size}'/>" +
		"</span>",

	// _imagePaths: [protected] Map
	//		This is stores the path to the palette images used for high-contrast mode display
	_imagePaths: {
		"7x10": "dijit/themes/a11y/colors7x10.png",
		"3x4": "dijit/themes/a11y/colors3x4.png"
	},

	constructor: function (alias: any, row: any, col: any, title: any) {
		// summary:
		//		Constructor for ColorPalette._Color
		this._title = title;
		this._row = row;
		this._col = col;
		this.setColor(Color.named[alias]);
	},

	getValue: function () {
		// summary:
		//		Note that although dijit._Color is initialized with a value like "white" getValue() always
		//		returns a hex value
		return this.toHex();
	},

	fillCell: function (/*DOMNode*/ cell: any, /*String*/ blankGif: any) {
		var html = string.substitute(has("highcontrast") ? this.hcTemplate : this.template, {
			// substitution variables for normal mode
			color: this.toHex(),
			blankGif: blankGif,
			alt: this._title,
			title: this._title,

			// variables used for high contrast mode
			image: this._imagePaths[this.palette].toString(),
			left: this._col * -20 - 5,
			top: this._row * -20 - 5,
			size: this.palette == "7x10" ? "height: 145px; width: 206px" : "height: 64px; width: 86px"
		});

		domConstruct.place(html, cell);
	}
});

export default ColorPalette;
